import {
  DRAFT_TTL_MS,
  pruneStorage,
  readRow,
  writeRow,
  type DraftStorage
} from './draftStorage';
import type { DraftCell, DraftFieldCatalog, DraftRow, DraftValues } from './draftTypes';

/**
 * Row drafts, held OUTSIDE React.
 *
 * The third instance of a pattern this module already owns twice
 * (`live/createLiveStore`, `live/createValueStore`): a vanilla store, one
 * listener set per key, `version` + `cachedSnapshot` so `useSyncExternalStore`
 * cannot tear.
 *
 * # Why the state cannot live in the row
 *
 * Both tables are virtualised. Scrolling a row out of view UNMOUNTS it, and a
 * `useState` inside it dies with it — twenty rows configured and then
 * confirmed is not something the operator can do today, and nothing tells them
 * why. Holding the draft outside React makes the virtualiser irrelevant.
 *
 * # One divergence from `createLiveStore`
 *
 * That one defers its notify to `requestAnimationFrame` because it takes two
 * frames a second across 300 rows. This one notifies SYNCHRONOUSLY: a
 * keystroke is one row at ~10/s, and deferring it makes a controlled
 * `TextField` fight the caret.
 */

export interface DraftStoreConfig {
  scope: string;
  fields: DraftFieldCatalog;
  /** Defaults to `window.localStorage` when present; memory otherwise. */
  storage?: DraftStorage | null;
  /** Injectable clock — the TTL tests need determinism. */
  now?: () => number;
  ttlMs?: number;
}

export interface DraftStore {
  readonly scope: string;
  readonly fields: DraftFieldCatalog;

  /** `useSyncExternalStore` contract, PER ROW. */
  subscribeRow(rowKey: string, onChange: () => void): () => void;
  /** Referentially stable between real changes. `undefined` = no draft. */
  getRowSnapshot(rowKey: string): DraftRow | undefined;

  /** The operator edited a control. */
  set(rowKey: string, field: string, value: unknown): void;
  /**
   * Write values as ALREADY CONFIRMED and untouched — a bulk apply staging a
   * rule-less row. No ✓ appears and no rule auto-creates, which is the
   * contract the bulk seed has today.
   */
  seed(rowKey: string, values: DraftValues, baseline?: DraftValues): void;
  /** A write landed: adopt the server's values and drop the interaction. */
  confirm(rowKey: string, values: DraftValues, baseline?: DraftValues): void;
  /** A write failed: keep what was typed, start the TTL from now. */
  fail(rowKey: string, fields: readonly string[]): void;
  clear(rowKey: string): void;
  clearFields(rowKey: string, fields: readonly string[]): void;

  /**
   * Drop cells the row no longer needs: overrides the baseline has caught up
   * with, and anything expired. Call from an effect, never during render.
   */
  reconcile(rowKey: string, baseline: DraftValues): void;

  /** Sweep `localStorage` for stale rows. Cheap; call on load / on focus. */
  prune(): void;

  /**
   * Drop every row this store holds, in memory and on disk.
   *
   * The store is a module singleton, which is what lets a draft outlive the
   * row that wrote it — and in a test file that same property leaks state from
   * one case into the next. Tests call this in `beforeEach`; production has no
   * reason to (drafts are per pair and expire on their own).
   */
  resetAll(): void;

  rowKeys(): string[];
  size(): number;
}

interface RowEntry {
  row: DraftRow;
  listeners: Set<() => void>;
  cachedSnapshot: DraftRow | undefined;
  cachedVersion: number;
  /** Row has been read back from storage. Hydration is lazy, per row. */
  hydrated: boolean;
}

function memoryStorage(): DraftStorage {
  const map = new Map<string, string>();
  return {
    getItem: k => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, v),
    removeItem: k => void map.delete(k),
    key: i => Array.from(map.keys())[i] ?? null,
    get length() {
      return map.size;
    }
  };
}

function resolveStorage(configured: DraftStorage | null | undefined): DraftStorage {
  if (configured) return configured;
  if (configured === null) return memoryStorage();
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    /* private mode can throw on ACCESS, not just on write */
  }
  return memoryStorage();
}

const PERSIST_DEBOUNCE_MS = 300;

export function createDraftStore(config: DraftStoreConfig): DraftStore {
  const { scope, fields } = config;
  const now = config.now ?? (() => Date.now());
  const ttlMs = config.ttlMs ?? DRAFT_TTL_MS;
  const storage = resolveStorage(config.storage);

  const entries = new Map<string, RowEntry>();
  const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();

  function entryFor(rowKey: string): RowEntry {
    let e = entries.get(rowKey);
    if (!e) {
      e = {
        row: { cells: {}, version: 0 },
        listeners: new Set(),
        cachedSnapshot: undefined,
        cachedVersion: -1,
        hydrated: false
      };
      entries.set(rowKey, e);
    }
    // Lazy hydration: parsing 300 keys up front would block the first paint,
    // and the virtualiser only ever mounts a couple of dozen rows.
    if (!e.hydrated) {
      e.hydrated = true;
      const stored = readRow(storage, scope, rowKey, fields, now());
      if (stored) {
        e.row = stored;
        e.row.version = 1;
      }
    }
    return e;
  }

  function notify(e: RowEntry): void {
    e.row.version += 1;
    for (const l of e.listeners) l();
  }

  function schedulePersist(rowKey: string): void {
    const existing = persistTimers.get(rowKey);
    if (existing) clearTimeout(existing);
    persistTimers.set(
      rowKey,
      setTimeout(() => {
        persistTimers.delete(rowKey);
        flushRow(rowKey);
      }, PERSIST_DEBOUNCE_MS)
    );
  }

  function flushRow(rowKey: string): void {
    const e = entries.get(rowKey);
    writeRow(storage, scope, rowKey, e?.row, fields, now());
  }

  function flushAll(): void {
    for (const [rowKey, timer] of persistTimers) {
      clearTimeout(timer);
      flushRow(rowKey);
    }
    persistTimers.clear();
  }

  // A tab going away must not lose the last 300 ms of typing.
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushAll();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushAll);
  }

  function mutate(rowKey: string, fn: (row: DraftRow) => boolean): void {
    const e = entryFor(rowKey);
    if (!fn(e.row)) return;
    notify(e);
    schedulePersist(rowKey);
  }

  return {
    scope,
    fields,

    subscribeRow(rowKey, onChange) {
      const e = entryFor(rowKey);
      e.listeners.add(onChange);
      return () => {
        e.listeners.delete(onChange);
      };
    },

    getRowSnapshot(rowKey) {
      const e = entryFor(rowKey);
      if (Object.keys(e.row.cells).length === 0) return undefined;
      // MUST be referentially stable between real changes, or
      // `useSyncExternalStore` re-renders forever.
      if (e.cachedVersion !== e.row.version) {
        e.cachedSnapshot = { cells: e.row.cells, version: e.row.version };
        e.cachedVersion = e.row.version;
      }
      return e.cachedSnapshot;
    },

    set(rowKey, field, value) {
      if (!fields[field]) return;
      mutate(rowKey, row => {
        const prev = row.cells[field];
        if (prev?.touched && Object.is(prev.value, value)) return false;
        const next: DraftCell = {
          value,
          touched: true,
          touchedAt: now(),
          failedAt: null
        };
        // The two travel together: a `confirmed` without its anchor leaves
        // `refetched` running on one disjunct.
        if (prev && 'confirmed' in prev) next.confirmed = prev.confirmed;
        if (prev && 'baselineAtConfirm' in prev) {
          next.baselineAtConfirm = prev.baselineAtConfirm;
        }
        row.cells = { ...row.cells, [field]: next };
        return true;
      });
    },

    seed(rowKey, values, baseline) {
      this.confirm(rowKey, values, baseline);
    },

    confirm(rowKey, values, baseline) {
      mutate(rowKey, row => {
        const cells = { ...row.cells };
        let changed = false;
        for (const [field, value] of Object.entries(values)) {
          if (!fields[field]) continue;
          const cell: DraftCell = {
            value,
            confirmed: value,
            touched: false,
            touchedAt: now(),
            failedAt: null
          };
          if (baseline && field in baseline) {
            cell.baselineAtConfirm = baseline[field];
          }
          cells[field] = cell;
          changed = true;
        }
        if (!changed) return false;
        row.cells = cells;
        return true;
      });
    },

    fail(rowKey, failedFields) {
      mutate(rowKey, row => {
        const cells = { ...row.cells };
        let changed = false;
        const at = now();
        for (const field of failedFields) {
          const prev = cells[field];
          if (!prev) continue;
          cells[field] = { ...prev, failedAt: at };
          changed = true;
        }
        if (!changed) return false;
        row.cells = cells;
        return true;
      });
    },

    clear(rowKey) {
      mutate(rowKey, row => {
        if (Object.keys(row.cells).length === 0) return false;
        row.cells = {};
        return true;
      });
    },

    clearFields(rowKey, toClear) {
      mutate(rowKey, row => {
        const cells = { ...row.cells };
        let changed = false;
        for (const field of toClear) {
          if (field in cells) {
            delete cells[field];
            changed = true;
          }
        }
        if (!changed) return false;
        row.cells = cells;
        return true;
      });
    },

    reconcile(rowKey, baseline) {
      mutate(rowKey, row => {
        const cells = { ...row.cells };
        let changed = false;
        const at = now();
        for (const [field, cell] of Object.entries(cells)) {
          const expired = (cell.failedAt ?? cell.touchedAt) + ttlMs <= at;

          // A cell only earns its keep by DIFFERING from what the baseline
          // would render. One that agrees carries nothing, and keeping it is
          // not neutral: `value` and `diverges` read it instead of the
          // baseline, so it goes on shadowing every later server change with a
          // number the engine is not running and a ✓ that stays off.
          //
          // Gated on the field, NOT applied to everything: for a toggle,
          // "equal to the baseline" is an expressible intention (an explicit
          // OFF on a rule-less row), and retiring it would collapse `touched`
          // from interaction into comparison — see
          // `DraftFieldSpec.retireWhenEqualToBaseline`.
          const spec = fields[field];
          const retirable =
            spec?.kind === 'string' && spec.retireWhenEqualToBaseline === true;
          const agrees =
            retirable &&
            cell.touched &&
            (Object.is(cell.value, baseline[field]) ||
              // Re-touched back to its OWN confirmed value: the interaction
              // left nothing behind, so the cell is the pure-override case
              // again — and `refetched` can only retire that one once
              // `touched` is false.
              ('confirmed' in cell && Object.is(cell.value, cell.confirmed)));

          // The override bridges ack→refetch. It retires when the baseline
          // MOVES — not when it agrees with us. A refetch carrying someone
          // else's value (a bulk, a subgroup apply, another tab) is just as
          // much "the server caught up", and holding the override against it
          // is exactly how a row ends up pinned to a stale number.
          const refetched =
            !cell.touched &&
            'confirmed' in cell &&
            (Object.is(cell.confirmed, baseline[field]) ||
              ('baselineAtConfirm' in cell &&
                !Object.is(cell.baselineAtConfirm, baseline[field])));

          if (expired || agrees || refetched) {
            delete cells[field];
            changed = true;
          }
        }
        if (!changed) return false;
        row.cells = cells;
        return true;
      });
    },

    prune() {
      pruneStorage(storage, fields, now());
    },

    resetAll() {
      for (const timer of persistTimers.values()) clearTimeout(timer);
      persistTimers.clear();
      for (const [rowKey, e] of entries) {
        e.row = { cells: {}, version: e.row.version + 1 };
        e.cachedVersion = -1;
        writeRow(storage, scope, rowKey, undefined, fields, now());
        for (const l of e.listeners) l();
      }
      entries.clear();
    },

    rowKeys() {
      return [...entries.keys()];
    },

    size() {
      return entries.size;
    }
  };
}
